import useDetectCountry from '@hooks/useDetectCountry'
import useResolution from '@hooks/useResolution'
import { COUNTRY_WISE_PRICING } from '@lib/data/country-wise-pricing'
import { FastlyImage } from '@components/common/image'
import { defineMessage, Trans } from '@lingui/macro'
import { LinguiJSMessageDescriptor } from '@lib/types/common'
import { msgT } from '@lib/utils/i18n'
import MsgTrans from '@components/common/i18n/message-trans'

type ClassDetails = {
  id?: string
  heading: LinguiJSMessageDescriptor
  content: LinguiJSMessageDescriptor
  price: string
  image: {
    label: string
    alt: LinguiJSMessageDescriptor
  }
}

export const getClassDetails = (currency: string): ClassDetails[] => {
  const formatPrice = (sectionId: string) => {
    // if the timezone is Asia/bangkok, show indonesian timezone
    if (currency === 'THB') {
      return `IDR ${COUNTRY_WISE_PRICING['IDR'][sectionId]}`
    }
    // if country not found, show USD pricing
    if (!COUNTRY_WISE_PRICING[currency]) {
      return `USD ${COUNTRY_WISE_PRICING['USD'][sectionId]}`
    }
    return `${currency} ${COUNTRY_WISE_PRICING[currency][sectionId]}`
  }

  return [
    {
      id: '1_on_1',
      heading: defineMessage({ message: `Private 1-on-1` }),
      content: defineMessage({
        message: `Our 1-on-1 coding classes for kids are suitable for children who want a full personalized and effective learning experience.`,
      }),
      price: formatPrice('1:1-retail-price'),
      image: {
        label: 'one-on-one-class',
        alt: defineMessage({ message: `Private 1-on-1 classes` }),
      },
    },
    {
      id: '1_on_2',
      heading: defineMessage({ message: `Peer learning program` }),
      content: defineMessage({
        message: `Max 3 learners per class. These classes for kids are perfect for children who want to learn coding together in an interactive and social setting.
`,
      }),
      price: formatPrice('1:2-retail-price'),
      image: {
        label: 'group-class',
        alt: defineMessage({ message: `Group of 3 classes` }),
      },
    },
  ]
}
const ClassesOverview = () => {
  const { sizes } = useResolution()
  const { currency } = useDetectCountry({
    isDeterminedViaSystemTimezoneOnly: true,
  })

  return (
    <div
      className="py-10 px-5 md:px-3 rounded-t-3xl"
      style={{
        background: !sizes.sm
          ? 'radial-gradient(110% 10% at top, transparent 50%, #FFF7F5 51%)'
          : '#FFF7F5',
      }}
    >
      <div className="w-full mx-auto text-center lg:max-w-6xl">
        <h3 className="my-10 text-2xl leading-tight text-grey lg:text-4xl font-700">
          <Trans id="homepage.classesOverview.heading">
            Learn coding <span className="underlined-text">privately</span> or{' '}
            <span className="underlined-text">with a buddy</span>
          </Trans>
        </h3>

        <div className="flex flex-col justify-center space-y-16 md:flex-row lg:space-y-0">
          {getClassDetails(currency).map((item, index) => (
            <div
              key={index}
              className="flex flex-col items-center mx-5 md:mx-8 space-y-4"
            >
              <FastlyImage
                src={`/images/home/group-classes/${item?.image?.label}.png`}
                alt={msgT(item.image.alt)}
                height={160}
                width={200}
              />
              <h3 className="text-2xl font-700">
                <MsgTrans msg={item.heading} />
              </h3>
              <p className="max-w-sm text-lg leading-6 text-grey">
                <MsgTrans msg={item.content} />
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ClassesOverview
